"use client";
import { IResource, ISchedule, ResourceType } from "@gettactic/api";
import {
  getDefaultsHoursFromOffice,
  UseDisclosureReturn
} from "@gettactic/helpers";
import { formatApiDatetime } from "@gettactic/helpers/src/date-time/formatApiDatetime";
import { IS_POST_RESERVATION_VISITOR_PROMPT_ENABLED } from "@gettactic/helpers/src/featureFlags";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { useCloseAnimationTimeout } from "@gettactic/helpers/src/hooks/useCloseAnimationTimeout";
import { useGlobalEscapeKey } from "@gettactic/helpers/src/hooks/useGlobalEscapeKey";
import { isResourceTicketGated } from "@gettactic/helpers/src/resources/isResourceTicketGated";
import { calculateReservationDefaults } from "@gettactic/helpers/src/schedules/calculateReservationDefaults";
import {
  useMapSchedulable,
  useOrgFeatureFlag,
  useRoomReservationWeb
} from "@gettactic/hooks";
import { addMinutes } from "date-fns";
import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-hot-toast";
import { MapDailyViewCard } from "@/features/map/components/MapDailyCalendarViewCard/MapDailyViewCard";
import { useMap } from "@/features/map/MapProvider";
import { ReserveFormProvider } from "@/features/reserve/components/Reserve/ReserveFormProvider";
import { ReserveOptions } from "@/features/reserve/components/Reserve/types";
import { useReserve } from "@/features/reserve/providers/context";
import { useRoomSelection } from "@/features/reserve/providers/roomSelectionContext";
import {
  ExternalGuestInfo,
  PostReservationVisitorPrompt
} from "@/features/visitors/components/PostReservationVisitorPrompt";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";
import { MapReserveCardContent } from "./MapReserveCardContent";
export interface MapReserveCardProps
  extends Omit<ReserveOptions, "resource" | "resourceType"> {
  resourceType?: ResourceType;
  resource?: IResource | null;
  onCancel?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  dayViewDisclosure?: UseDisclosureReturn;
  requireDelegatedPermission?: boolean;
  msAuthUrl?: string;
  isCompact?: boolean;
  isModal?: boolean;
  onSuccess?: () => void;
  isOnReservationModal?: boolean;
  reservationCardIsOpen?: boolean;
  editSchedule?: ISchedule;
}

export const MapReserveCard: React.FC<MapReserveCardProps> = (props) => {
  const {
    reserveData: reserveProps,
    resource: reloadedResource,
    closeReserveModal,
    isReserveOpen,
    openReserveModal
  } = useReserve();

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const wpRequestsRef = React.useRef<WorkplaceRequestsRef | null>(null);
  const isFull = isMd && !props.isCompact;
  // Sticky for the whole reserve session: dropping back to a single room must not
  // remount the form and throw away what the user typed. The selection owns the flag —
  // it cannot be watched from here, because a selection that restarts on another room
  // clears and re-adds in one go and never renders the empty state in between.
  const {
    rooms: selectedRooms,
    clearRooms,
    isMultiRoomSession
  } = useRoomSelection();
  const hasMultipleRooms = selectedRooms.length > 1 || isMultiRoomSession;

  // Visitor management: prompt to pre-register external guests after reservation
  const visitorManagementFlag = useOrgFeatureFlag("visitor_management");
  const [visitorPromptData, setVisitorPromptData] =
    useState<ExternalGuestInfo | null>(null);

  const resource = reloadedResource || reserveProps?.resource;

  // The form's key while in single-room mode, frozen the instant a second room
  // joins the reservation. Reading `resource?.id` directly for that case would
  // make the key jump from a defined id to `undefined` in the very render that
  // adds the second room — a key change that remounts ReserveFormProvider and
  // wipes whatever the user had already typed.
  const formKeyRef = React.useRef<string | undefined>(resource?.id);
  if (!hasMultipleRooms) {
    formKeyRef.current = resource?.id;
  }

  const isOpen = isReserveOpen && !!reserveProps && !!resource;

  // Track current form time/duration to preserve when switching resources
  const currentFormTimeRef = React.useRef<{
    time: Date;
    duration: number;
  } | null>(null);

  // Callback to update ref when form time changes
  const handleFormTimeChange = React.useCallback(
    (time: Date) => {
      if (currentFormTimeRef.current) {
        currentFormTimeRef.current.time = time;
      } else {
        currentFormTimeRef.current = {
          time,
          duration: reserveProps?.duration || 60
        };
      }
    },
    [reserveProps?.duration]
  );

  // Callback to update ref when form duration changes
  const handleFormDurationChange = React.useCallback(
    (duration: number) => {
      if (currentFormTimeRef.current) {
        currentFormTimeRef.current.duration = duration;
      } else {
        currentFormTimeRef.current = {
          time: reserveProps?.time || new Date(),
          duration
        };
      }
    },
    [reserveProps?.time]
  );

  // Initialize ref when reserveProps are set initially
  React.useEffect(() => {
    if (
      reserveProps?.time &&
      reserveProps?.duration &&
      !currentFormTimeRef.current
    ) {
      currentFormTimeRef.current = {
        time: reserveProps.time,
        duration: reserveProps.duration
      };
    }
  }, [reserveProps?.time, reserveProps?.duration]);

  const resetToInitialTime = () => {
    const initialTime = closeReserveModal();
    if (initialTime) {
      // they only get updated together for now
      if (initialTime.mapStart || initialTime.mapEnd) {
        changeStartDate(initialTime.mapStart || null);
        changeEndTime(initialTime.mapEnd || null);
      } else {
        changeStartDate(initialTime.start || null);
        changeEndTime(
          addMinutes(
            initialTime.start || new Date(),
            initialTime.duration || 0
          ) || null
        );
      }
    }
  };

  const onClose = () => {
    // User clicked back button or escape - reset to initial time and close
    events.trigger("onFocusResource", null);
    resetToInitialTime();
    currentFormTimeRef.current = null;
    clearRooms();
  };

  useGlobalEscapeKey(onClose);

  const { isVisible } = useCloseAnimationTimeout(isOpen);
  const {
    requireDelegatedPermission,
    msAuthUrl,
    onRoomChange,
    onOrganizerChange,
    organizerRequiresDelegatedAccess,
    hasMSCalendar,
    hasGoogleCalendar
  } = useRoomReservationWeb({
    isOpen: isVisible,
    isRoomReservation: resource?.type === "meeting_room"
  });

  const {
    events,
    startDate,
    endDate,
    floor,
    currentOffice,
    timeMode,
    mapInstanceId,
    changeStartDate,
    changeEndTime
  } = useMap();

  const response = useMapSchedulable({
    officeId: currentOffice.id,
    parentId: floor?.id ?? null,
    startDate: startDate ? formatApiDatetime(startDate) : "",
    endDate: endDate ? formatApiDatetime(endDate) : "",

    // it's available from the new map
    enabled:
      !!startDate &&
      !!endDate &&
      !!floor &&
      !!currentOffice.id &&
      !!resource?.id
  });

  const haveAvailableSlots = useMemo(() => {
    return (
      response.schedulable &&
      response.schedulable.length > 0 &&
      (response.schedulable.find((r) => r.id === resource?.id)
        ?.available_slots ?? 0) > 0
    );
  }, [response?.schedulable, resource?.id]);

  React.useEffect(() => {
    const onFocusResource = (resource: IResource | null) => {
      if (!resource) {
        closeReserveModal();
        // Don't reset time here - time reset is handled by:
        // 1. onClose (back button/escape) or
        // 2. onSuccess (form submit)
        // Not resetting here prevents losing time changes when switching resources
        props.dayViewDisclosure?.onOpenChange(false);
        currentFormTimeRef.current = null;
        return;
      }

      // Walk-up resources are never reservable from the app — their
      // occupancy is managed externally via the API. This is the single
      // choke point every "start reservation" path funnels through, so
      // blocking it here covers map clicks, cmd/ctrl-click, and the
      // resource details drawer's Reserve button in one place.
      if (resource.is_walkup) {
        return;
      }

      // A ticket-gated resource is booked only by filing one of its request
      // types, so every entry point (sidebar list, search, deep link,
      // cmd/ctrl-click) must land on the same wizard the details popover's
      // "Request to Book" opens instead of the direct reservation form.
      if (isResourceTicketGated(resource)) {
        events.trigger("onRequestGatedBooking", resource);
        return;
      }

      // Determine which time to use
      let _startDate: Date | undefined;
      let _duration: number;

      // When switching resources and modal is already open, preserve current time
      if (isReserveOpen && currentFormTimeRef.current) {
        // Use the tracked form time which includes user's changes
        _startDate = currentFormTimeRef.current.time;
        _duration = currentFormTimeRef.current.duration;
      } else if (isReserveOpen && reserveProps?.time) {
        // Editing existing reservation: use existing reservation times
        _startDate = reserveProps.time;
        _duration = reserveProps.duration || 60;
      } else {
        // For new reservations: calculate defaults from map filter and office settings
        const defaultHours = getDefaultsHoursFromOffice(currentOffice);
        const defaults = calculateReservationDefaults({
          timeMode,
          mapStartDate: startDate,
          mapEndDate: endDate,
          resourceType: resource.type,
          defaultHours
        });
        _startDate = defaults.startDate;
        _duration = defaults.duration;
      }

      // When in edit mode, preserve reservation data (title, attenders, etc.)
      // that would otherwise be lost when switching resources on the map
      const editDataToPreserve =
        props.editSchedule && reserveProps
          ? {
              title: reserveProps.title,
              attenders: reserveProps.attenders,
              owner: reserveProps.owner,
              workplaceRequestItems: reserveProps.workplaceRequestItems
            }
          : {};

      openReserveModal({
        start: _startDate || undefined,
        time: _startDate,
        resource: resource,
        duration: _duration,
        floorId: floor?.id ?? null,
        resourceType: resource.type,
        office: currentOffice,
        mapStart: startDate,
        mapEnd: endDate,
        ...editDataToPreserve
      });
    };
    events.register("onFocusResource", onFocusResource);

    return () => {
      events.unregister("onFocusResource", onFocusResource);
    };
  }, [
    startDate,
    endDate,
    floor,
    currentOffice,
    isReserveOpen,
    timeMode,
    reserveProps
  ]);

  return (
    <>
      <ReserveFormProvider
        // Remounting per resource would wipe the form when the card moves between the
        // rooms of a multi-room reservation, so the key is held stable in that case —
        // via the frozen ref above, not a literal `undefined`, so the transition into
        // multi-room mode itself doesn't look like a key change.
        key={props.isOnReservationModal ? undefined : formKeyRef.current}
        onStartTimeChange={handleFormTimeChange}
        onDurationChange={handleFormDurationChange}
        onSuccess={() => {
          // Reset to initial time on successful submit
          resetToInitialTime();
          currentFormTimeRef.current = null;
          clearRooms();
          props.onSuccess?.();
          toast.success(
            props.editSchedule
              ? "Reservation updated successfully"
              : "Reservation created successfully"
          );
        }}
        onExternalGuestsDetected={
          IS_POST_RESERVATION_VISITOR_PROMPT_ENABLED && visitorManagementFlag
            ? setVisitorPromptData
            : undefined
        }
        onRoomChange={onRoomChange}
        onOrganizerChange={onOrganizerChange}
        organizerRequiresDelegatedAccess={organizerRequiresDelegatedAccess}
        hasMSCalendar={hasMSCalendar}
        hasGoogleCalendar={hasGoogleCalendar}
        wpRequestsRef={wpRequestsRef}
        requireDelegatedPermission
        {...reserveProps}
        resource={resource}
        schedule={props.editSchedule}
        isOnReservationModal={props.isOnReservationModal}
        isReservationCardIsOpen={isVisible}
        shouldLoadResources={isVisible}
      >
        {props.dayViewDisclosure &&
          createPortal(
            <MapDailyViewCard
              isOpen={props.dayViewDisclosure.isOpen}
              onOpenChange={props.dayViewDisclosure.onOpenChange}
              office={currentOffice}
              isModal={props.isModal}
              isReserving={isReserveOpen}
            />,
            window.document.querySelector(
              `#map-daily-view-card-holder-${mapInstanceId.replace(/:/g, "")}`
            ) || document.body
          )}
        <MapReserveCardContent
          {...props}
          resource={resource!}
          haveAvailableSlots={haveAvailableSlots}
          onClose={onClose}
          isMd={isMd}
          isFull={isFull}
          isOpen={isOpen}
          msAuthUrl={msAuthUrl}
          requireDelegatedPermission={requireDelegatedPermission}
          isVisible={isVisible}
        />
      </ReserveFormProvider>
      {visitorPromptData && (
        <PostReservationVisitorPrompt
          guestInfo={visitorPromptData}
          onClose={() => setVisitorPromptData(null)}
        />
      )}
    </>
  );
};
